import React, { useState } from 'react';
import { Check } from 'lucide-react';
import type { FormField, OptionLayout, ThemeConfig } from '../../../types/forms';
import { normalizeFieldOption } from '../../../types/forms';

const LAYOUT_CLASSES: Record<OptionLayout, string> = {
  vertical: 'space-y-3',
  horizontal: 'flex flex-wrap gap-3',
  'grid-2': 'grid grid-cols-1 md:grid-cols-2 gap-3',
  'grid-3': 'grid grid-cols-1 md:grid-cols-3 gap-3',
};

const LETTER_KEYS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

interface CheckboxFieldProps {
  field: FormField;
  value?: string[];
  onChange?: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
  themeConfig?: ThemeConfig;
}

export const CheckboxField: React.FC<CheckboxFieldProps> = ({
  field,
  value = [],
  onChange,
  error,
  disabled,
  themeConfig,
}) => {
  const allowOther = !!field.config?.allowOther;
  const otherValue = value.find(v => !field.options?.some(o => normalizeFieldOption(o).value === v));
  const [otherText, setOtherText] = useState(otherValue || '');

  const accentColor = themeConfig?.buttonBgColor || '#3b82f6';
  const textColor = themeConfig?.textColor || '#ffffff';

  const handleToggle = (option: string) => {
    if (disabled) return;
    const newValue = value.includes(option)
      ? value.filter(v => v !== option)
      : [...value, option];
    onChange?.(newValue);
  };

  // Simple boolean checkbox (no options)
  if (!field.options || field.options.length === 0) {
    const isChecked = value.length > 0 && value[0] === 'true';

    return (
      <label
        className={`
          flex items-center gap-3 cursor-pointer group
          w-full min-h-13 px-4 py-3 rounded-xl border
          transition-all duration-200
          touch-manipulation
        `}
        style={{
          borderColor: isChecked ? accentColor : error ? '#ef4444' : 'rgba(115,115,115,0.5)',
          backgroundColor: isChecked ? `${accentColor}15` : 'transparent',
        }}
      >
        <div className="relative shrink-0">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => onChange?.(e.target.checked ? ['true'] : [])}
            disabled={disabled}
            className="sr-only peer"
          />
          <div
            className="w-7 h-7 rounded-lg border-2 flex items-center justify-center transition-all duration-200"
            style={{
              borderColor: isChecked ? accentColor : 'rgba(115,115,115,0.6)',
              backgroundColor: isChecked ? accentColor : 'transparent',
            }}
          >
            {isChecked && <Check className="w-4 h-4 text-white" />}
          </div>
        </div>
        <span className="text-base" style={{ color: textColor }}>{field.label}</span>
      </label>
    );
  }

  // Multi-option checkboxes
  const optionLayout = (field.config?.optionLayout as OptionLayout) || 'vertical';
  const isOtherChecked = allowOther && !!otherValue;

  const handleToggleOther = () => {
    if (disabled) return;
    if (isOtherChecked) {
      onChange?.(value.filter(v => v !== otherValue));
    } else {
      onChange?.([...value, otherText]);
    }
  };

  return (
    <div className={LAYOUT_CLASSES[optionLayout] || 'space-y-3'}>
      {field.options.map((rawOption, index) => {
        const opt = normalizeFieldOption(rawOption);
        const isChecked = value.includes(opt.value);
        const letter = LETTER_KEYS[index] || '';

        return (
          <label
            key={opt.value}
            className={`
              flex items-center gap-3 cursor-pointer group
              w-full min-w-0 min-h-13 px-4 py-3 rounded-xl border
              transition-all duration-200
              touch-manipulation
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            style={{
              borderColor: isChecked ? accentColor : error ? '#ef4444' : 'rgba(115,115,115,0.5)',
              backgroundColor: isChecked ? `${accentColor}15` : 'transparent',
            }}
          >
            <div className="relative shrink-0">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => handleToggle(opt.value)}
                disabled={disabled}
                className="sr-only peer"
              />
              <div
                className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-semibold transition-all duration-200"
                style={{
                  borderColor: isChecked ? accentColor : 'rgba(115,115,115,0.6)',
                  backgroundColor: isChecked ? accentColor : 'transparent',
                  color: isChecked ? '#ffffff' : textColor,
                }}
              >
                {isChecked ? <Check className="w-4 h-4" /> : letter}
              </div>
            </div>
            <span className="text-base min-w-0 break-words" style={{ color: textColor }}>{opt.label}</span>
          </label>
        );
      })}

      {allowOther && (
        <label
          className={`
            flex items-center gap-3 cursor-pointer group
            w-full min-w-0 min-h-13 px-4 py-3 rounded-xl border
            transition-all duration-200
            touch-manipulation
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          style={{
            borderColor: isOtherChecked ? accentColor : error ? '#ef4444' : 'rgba(115,115,115,0.5)',
            backgroundColor: isOtherChecked ? `${accentColor}15` : 'transparent',
          }}
        >
          <div className="relative shrink-0">
            <input
              type="checkbox"
              checked={isOtherChecked}
              onChange={handleToggleOther}
              disabled={disabled}
              className="sr-only peer"
            />
            <div
              className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-semibold transition-all duration-200"
              style={{
                borderColor: isOtherChecked ? accentColor : 'rgba(115,115,115,0.6)',
                backgroundColor: isOtherChecked ? accentColor : 'transparent',
                color: isOtherChecked ? '#ffffff' : textColor,
              }}
            >
              {isOtherChecked ? <Check className="w-4 h-4" /> : LETTER_KEYS[field.options.length]}
            </div>
          </div>
          <span className="text-base min-w-0 break-words" style={{ color: textColor }}>Outro</span>
          {isOtherChecked && (
            <input
              type="text"
              value={otherText}
              onChange={(e) => {
                setOtherText(e.target.value);
                onChange?.(value.filter(v => v !== otherValue).concat(e.target.value));
              }}
              placeholder="Digite sua resposta..."
              disabled={disabled}
              className="flex-1 bg-transparent border-b border-neutral-600 text-base outline-none focus:border-primary-500 px-1 py-0.5"
              style={{ color: textColor }}
            />
          )}
        </label>
      )}
    </div>
  );
};
